// ⌘K / Ctrl+K (Figma "Web · Command"): jump to any page, or ask the coach,
// from anywhere in the app. Type to narrow; ↑ ↓ and Enter, or click.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

export function paletteItems({ isAdmin = false } = {}) {
  return [
    { label: 'Ask your coach', hint: 'Coach', run: 'coach' },
    { label: 'Today', hint: 'Page', to: '/dashboard' },
    { label: 'Plan', hint: 'Page', to: '/calendar' },
    { label: 'Stats', hint: 'Page', to: '/stats' },
    { label: 'Activities', hint: 'Page', to: '/activities' },
    { label: 'Aithlo Shift', hint: 'Page', to: '/shift' },
    { label: 'Workouts', hint: 'Library', to: '/workouts' },
    { label: 'You', hint: 'Profile', to: '/profile' },
    { label: 'Settings', hint: 'Account', to: '/settings' },
    ...(isAdmin ? [
      { label: 'Routes', hint: 'Library', to: '/route-editor' },
      { label: 'Users', hint: 'Admin', to: '/admin/users' },
      { label: 'AI usage', hint: 'Admin', to: '/admin/ai-usage' },
      { label: 'Coach console', hint: 'Admin', to: '/admin/coach' },
      { label: 'Admin', hint: 'Admin', to: '/admin' },
    ] : []),
  ];
}

export function matchItems(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((i) => `${i.label} ${i.hint}`.toLowerCase().includes(q));
}

export default function CommandPalette() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(0);
  const listRef = useRef(null);
  const items = useMemo(() => matchItems(paletteItems({ isAdmin: user?.tier === 'admin' }), query), [user, query]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (open) { setQuery(''); setAt(0); } }, [open]);
  useEffect(() => { setAt(0); }, [query]);

  const go = (item) => {
    setOpen(false);
    if (item.run === 'coach') window.dispatchEvent(new CustomEvent('chat:open'));
    else navigate(item.to);
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(items.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter' && items[at]) { e.preventDefault(); go(items[at]); }
  };
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' }); }, [at]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[10040] bg-black/50" />
        <DialogPrimitive.Content aria-describedby={undefined}
          className="fixed left-1/2 top-[18vh] z-[10041] w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2 overflow-hidden rounded-[20px] border border-border-default bg-bg-panel font-body shadow-[0_24px_64px_rgba(0,0,0,0.45)] focus:outline-none">
          <DialogPrimitive.Title className="sr-only">Go to</DialogPrimitive.Title>
          <div className="flex items-center gap-3 border-b border-border-subtle px-4">
            <Search size={18} className="shrink-0 text-text-tertiary" aria-hidden="true" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Go to a page, or ask your coach…" aria-label="Go to"
              role="combobox" aria-expanded="true" aria-controls="command-list"
              className="h-14 flex-1 bg-transparent type-body text-text-primary outline-none placeholder:text-text-tertiary" />
            <span className="rounded-md border border-border-default px-1.5 py-0.5 type-caption text-text-tertiary">esc</span>
          </div>
          <ul id="command-list" ref={listRef} role="listbox" className="m-0 max-h-[50vh] list-none overflow-y-auto p-2">
            {items.length === 0 && <li className="px-3 py-6 text-center type-body-small text-text-tertiary">Nothing matches.</li>}
            {items.map((item, i) => (
              <li key={item.label} role="option" aria-selected={i === at}>
                <button type="button" onClick={() => go(item)} onMouseEnter={() => setAt(i)}
                  className={cn('flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left',
                    i === at ? 'bg-bg-raised text-text-primary' : 'text-text-body')}>
                  <span className="type-label-section">{item.label}</span>
                  <span className="type-caption text-text-tertiary">{item.hint}</span>
                </button>
              </li>
            ))}
          </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
